import {ChangeDetectionStrategy, Component, Inject, InjectionToken, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {
  debounceTime,
  groupBy,
  map,
  mergeMap,
  skip,
  startWith,
  switchMap,
  take, takeUntil,
  toArray
} from "rxjs/operators";
import {WidgetFilter} from "../../core/models";
import {Store} from "@ngxs/store";
import {BehaviorSubject, from, Subject} from "rxjs/index";
import {SelectionModel} from "@angular/cdk/collections";
import {CoreService} from "../../core/services/core.service";

export interface GroupSelection {
  all: boolean;
  some: boolean;
  display: boolean;
}

export interface University {
  id: number;
  name: string;
  fullname: string;
  country: number | string;
  selectable: boolean;
}


@Component({
  selector: 'app-filter-university-dialog',
  templateUrl: './filter-university-dialog.component.html',
  styleUrls: ['./filter-university-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterUniversityDialogComponent implements OnInit {

  buttonLabel: string = 'Добавить';
  singleMode: boolean;

  form: FormGroup;
  universities = [];

  groups: Array<Array<University>> = [];
  filteredGroups$ = new BehaviorSubject([]);
  filter: FormControl = new FormControl('');
  selection: SelectionModel<University>;
  groupSelected: Array<GroupSelection> = [];
  allSelected: GroupSelection = {all: false, some: false, display: true};
  varLabel: string = '';
  isValid: boolean = false;
  loading: boolean = true;
  destroyed$ = new Subject();
  op = '=';

  indicators: [];
  filters: [];
  keys: [];

  constructor(private formBuilder: FormBuilder,
              private store: Store,
              private coreService: CoreService,
              private dialogRef: MatDialogRef<FilterUniversityDialogComponent>,
              @Inject(MAT_DIALOG_DATA) private data: any) {
    this.singleMode = this.data['singleMode'] || false;
    delete this.data['singleMode'];

    this.selection = new SelectionModel<University>(!this.singleMode, []);
    this.indicators = data && data.indicators ? data.indicators : [];
    this.filters = data && data.filters ? data.filters : [];
    this.keys = this.indicators.map(v => v['indicator']) as [];

    this.prepareOptions();
  }

  ngOnInit(): void {

    this.filter.valueChanges.pipe(
      takeUntil(this.destroyed$),
      startWith(''),
      debounceTime(350),
      map(value => this._filterValue(value))
    ).subscribe(value => {
      this.filteredGroups$.next(value);
    });

    this.selection.changed.subscribe(v => {
      if (this.selection.selected.length == 1) {
        this.varLabel = this.selection.selected[0].fullname;
      } else if (this.selection.selected.length) {
        this.varLabel = `${this.selection.selected[0].fullname} и ещё ${this.selection.selected.length - 1}`;
      } else {
        this.varLabel = '';
      }

      if(this.op == 'NOT IN') {
        this.varLabel = '!= ' + this.varLabel;
      }

      this.isValid = this.selection.selected.length > 0;

    });

    if (this.data && this.data.val) {
      this.buttonLabel = 'Сохранить';
      this.op = this.data.op || '=';

/*
      const universities = (this.data.val as string).split(';');
      // for (let i = 0; i < universities.length; i++) {
      //
      //   const name = universities[i];

        this.filteredGroups$
          .pipe(
            switchMap((v: Array<Array<University>>) => from(v)
              .pipe(
              )),
            concatAll(),
            filter((u: University) => {
              // console.log(u.name);
              return universities.includes(u.name);
            }),
            // take(1)
          ).subscribe({
            next: u => {
              console.log(u);
              this.selection.select(u);
            },
            // complete: () => {
            //   console.log('complete');
            //   for (let i = 0; i < this.groupSelected.length; i++) {
            //     this.isSomeSelected(this.groups[i], i);
            //   }
            // }
          }
          );//.unsubscribe();
      // }
      */
    }

    if (this.singleMode) {
      this.buttonLabel = 'Выбрать';
    }

    this.filteredGroups$.subscribe((v: University[][]) => {
      if (this.loading == false) {
        for (let i = 0; i < v.length; i++) {
          this.updateSomeSelected(v[i], i);
        }
      }
    });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  complete() {
    let payload: WidgetFilter = new WidgetFilter(
      'name',
      this.op,
      '',
      null,
      null,
      null,
      null,
      null);

    if (this.selection.selected.length > 1) {
      if(payload.op == '=' || payload.op == 'IN') {
        payload.op = 'IN';
      } else {
        payload.op = 'NOT IN';
      }
    }

    payload.val = this.selection.selected.map(v => v.name).join(';');
    const op = payload.op == 'NOT IN' ? '!=' : payload.op;
    const varLabel = `Университет ${op} ` + this.selection.selected.map(v => v.fullname).join('; ');

    this.dialogRef.close({
      data: {...payload, label: varLabel},
      universities: this.selection.selected
    });
  }

  prepareOptions() {
    const universities = (this.data && this.data.val) ? (this.data.val as string).split(';') : [];

    // this.universities$
    this.coreService.loadFiltered({'fields': this.indicators, 'filters': this.filters})
      .pipe(
        takeUntil(this.destroyed$),
        switchMap((v: Array<University>) => {
          this.groups = [];
          this.groupSelected = [];
          this.loading = false;
          return from(v).pipe(
            map((v: University) => {
              let selectable;
              if (this.keys != undefined) {
                selectable = true;
                for(let i=0 ; i<this.keys.length ; i++) {
                  selectable = selectable && v[this.keys[i]] != null;
                }
              } else {
                selectable = true;
              }

              return {
                id: v.id,
                name: v.name,
                fullname: v.fullname,
                country: v.country,
                selectable: selectable,
              };
            }),
            groupBy((v: University) => v.country),
            mergeMap(group => group.pipe(toArray())),
          )}
        ),
      )
      .subscribe(v => {
        const index = this.groups.length - 1;
        const display = index < 4;
        this.groups.push(v);
        this.groupSelected.push({all: false, some: false, display: display} as GroupSelection);

        for(let i=0 ; i<v.length ; i++) {
          if (universities.includes(v[i].name)) {
            this.selection.select(v[i]);
          }
        }

        if (!display) {
          this.loading = true;
          setTimeout(v => {
            if(this.groupSelected[index] != undefined) {
              this.groupSelected[index].display = true;
              const filterValue = typeof this.filter.value === 'string' ? this.filter.value : '';
              const groups = filterValue != '' ? this._filterValue(filterValue) : this.groups;
              this.filteredGroups$.next(groups);
              if (this.groups.length - 2 == index) {
                this.loading = false;
              }
            }
          }, 100 * index);
        }
      });
  }

  private _filterValue(value: any): Array<Array<University>> {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : '';
    let groups: Array<Array<University>> = [];
    if (filterValue == '') {
      groups = this.groups.slice(0);
    } else {
      // const countries = this.countries.filter((v: Country) => v.name.toLowerCase().indexOf(filterValue) !== -1 ).map(v => v.name);
      for (let i = 0; i < this.groups.length; i++) {
        // let universities = this.groups[i].filter((v: University) => v.name.toLowerCase().indexOf(filterValue) !== -1 || countries.includes(v.country))
        let universities = this.groups[i].filter((v: University) => v.fullname.toLowerCase().indexOf(filterValue) !== -1 || v.country.toString().toLowerCase().indexOf(filterValue) !== -1)
        groups.push(universities);
      }
    }
    return groups;
  }


  isSomeSelected(group, index): GroupSelection {
    let all = true;
    let some = false;
    for (let i = 0; i < group.length; i++) {
      const selected = this.selection.isSelected(group[i])
      all = all && selected;
      some = some || selected;
    }
    return {all: all, some: !all && some, display: true};
  }

  updateSomeSelected(group, index) {
    const res = this.isSomeSelected(group, index);
    this.groupSelected[index].all = res.all;
    this.groupSelected[index].some = res.some;
  }

  updateAllSelected() {
    if(this.singleMode) {
      return;
    }

    this.allSelected.all = true;
    this.allSelected.some = false;
    let all = false;
    let some = false;
    for(let i=0 ; i<this.groupSelected.length ; i++) {
      if(!this.groupSelected[i].all) {
        this.allSelected.all = false;
      }
      all = all || this.groupSelected[i].all;
      some = some || this.groupSelected[i].some;
    }
    if(this.allSelected.all) {
      this.allSelected.some = false
    } else {
      this.allSelected.some = some || all;
    }
  }

  toggle(groupIndex, row) {
    this.selection.toggle(row);

    this.filteredGroups$.pipe(
      switchMap((v: University[][]) => from(v)
        .pipe(
          skip(groupIndex),
          take(1)
        )
      )
    ).subscribe((v: University[]) => {
      this.updateSomeSelected(v, groupIndex);
      this.updateAllSelected();
    }).unsubscribe();
  }

  masterToggle(index) {
    if(this.singleMode) {
      return;
    }

    this.filteredGroups$.pipe(
      switchMap((v: University[][]) => from(v)
        .pipe(
          skip(index),
          take(1)
        )
      )
    ).subscribe((v: University[]) => {

      this.updateSomeSelected(v, index);

      for (let i = 0; i < v.length; i++) {
        if (this.groupSelected[index].all) {
          this.selection.deselect(v[i]);
        } else {
          this.selection.select(v[i]);
        }
      }

      this.updateSomeSelected(v, index);
      this.updateAllSelected();
    }).unsubscribe();

  }

  allToggle() {
    const all = this.allSelected.all;
    let index = 0;
    this.filteredGroups$.pipe(
      switchMap((v: University[][]) => from(v))
    ).subscribe((group: University[]) => {

      // this.updateSomeSelected(v, index);

      for (let i = 0; i < group.length; i++) {
        if (all) {
          this.selection.deselect(group[i]);
        } else {
          this.selection.select(group[i]);
        }
      }
      this.updateSomeSelected(group, index);
      index++;
    }).unsubscribe();
    this.allSelected.all = !all;
  }

  groupTrackByFn(index, item) {
    return `c-${index}`; // уникальный идентификатор, соответствующий элементу
  }

  universityTrackByFn(index, item) {
    return `u-${item.id}`; // уникальный идентификатор, соответствующий элементу
  }
}
